/* eslint-disable @typescript-eslint/indent */
/* eslint-disable indent */
import { Button, Input, message, Modal, Spin } from 'antd';
import React from 'react';
import s from './s.module.less';
import { getProviderNameDisplay } from 'utils/provider';
import useTimezone from 'hooks/useTimezone';
import dayjs from 'utils/dayjs';
import {
    ERescheduledAction,
    TScheduleAFollowUpVisitByProvider,
    TServerCalendarAppointmentEventDetail,
} from 'types/calendar';
import { EContactTypeV2, EServiceType, Prospect } from 'types/common';
import ProviderStore from 'store/Provider';
import TimePicker from 'components/TimePicker';
import { calendarScheduleAFollowUpVisitByProvider, getProviderTimeSlotByLength } from 'api/google';
import { createMeetingRoom } from 'api/zoom';
import useKlarityServiceTemplate from 'hooks/useKlarityServiceTemplate';
import useService from 'hooks/useService';
import useProviderKlarityServiceList from 'hooks/useProviderKlarityServiceList';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';

type Props = {
    open: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    appointmentEventDetail: TServerCalendarAppointmentEventDetail;
    targetClient: Prospect;
};

enum EScheduleUnit {
    DAYS = 'Days',
    WEEKS = 'Weeks',
    MONTHS = 'Months',
}

//not same ui with antd
const FollowUpModal = (props: Props) => {
    const { open, onCancel, onConfirm, appointmentEventDetail, targetClient } = props;
    const [selectScheduleItemIndex, setSelectScheduleItemIndex] = React.useState<number>(1);

    const [scheduleReminderValue, setScheduleReminderValue] = React.useState<number | undefined>(14);
    const [scheduleReminderUnit, setScheduleReminderUnit] = React.useState<string>(EScheduleUnit.DAYS);
    const [scheduleReminderTime, setScheduleReminderTime] = React.useState<number>();
    const [scheduleReminderText, setScheduleReminderText] = React.useState<string>();
    const scheduleReminderAreaRef = React.useRef<any>(null);

    const [scheduleSmartFollowUpValue, setScheduleSmartFollowUpValue] = React.useState<number | undefined>(14);
    const [scheduleSmartFollowUpUnit, setScheduleSmartFollowUpUnit] = React.useState<string>(EScheduleUnit.DAYS);
    const [scheduleSmartFollowUpTime, setScheduleSmartFollowUpTime] = React.useState<number>();
    const [scheduleSmartFollowUpText, setScheduleSmartFollowUpText] = React.useState<string>();

    const [defaultNextFollowUpTime, setDefaultNextFollowUpTime] = React.useState<number>();
    const [defaultRescheduleTime, setDefaultRescheduleTime] = React.useState<number>();

    const [emailSign, setEmailSign] = React.useState<string>();
    const [customizeText, setCustomizeText] = React.useState<string>();
    const [, myTimezone] = useTimezone();

    const [appointmentDate, setAppointmentDate] = React.useState<dayjs.Dayjs | undefined>(dayjs());
    const [showTimeSelectModal, setShowTimeSelectModal] = React.useState<boolean>(false);

    const [getUser] = ProviderStore.useStore();
    const timezone: string = myTimezone?.value as string;
    const [timeSlotList, setTimeSlotList] = React.useState<number[]>([]);
    const [loadingTimeSlot, setLoadingTimeSlot] = React.useState<boolean>(false);
    const [meetingLink, setMeetingLink] = React.useState<string>();
    const [submiting, setSubmiting] = React.useState<boolean>(false);
    const [hasEstablishedService, setHasEstablishedService] = React.useState<boolean | undefined>(undefined);
    const [klarityService, loadingKlarityService, refetchKlarityService] = useProviderKlarityServiceList();
    const followUpDuration = 15;
    const navigate = useNavigate();
    const timeZoneLabel = myTimezone?.value;
    const disabled = selectScheduleItemIndex === 1 && !hasEstablishedService;

    React.useEffect(() => {
        refetchKlarityService();
        const getMeetingLink = async () => {
            const res = await createMeetingRoom();
            if (!res.error) {
                setMeetingLink(res.data.data.meetingUrl);
            }
        };
        if (open) {
            getMeetingLink();
        }
    }, [open]);

    React.useEffect(() => {
        if (appointmentEventDetail && klarityService) {
            const spe = appointmentEventDetail.serviceSpecialty;
            let serviceList = klarityService;
            if (spe) {
                serviceList = klarityService.filter((item) => item.serviceSpecialtyName === spe);
            }
            const establishedService = serviceList.find(
                (item) => item.serviceType === EServiceType.ESTABLISHED_PATIENT_VISIT,
            );
            if (establishedService) {
                setHasEstablishedService(true);
            } else {
                setHasEstablishedService(false);
            }
        }
    }, [appointmentEventDetail, klarityService]);

    React.useEffect(() => {
        if (appointmentEventDetail) {
            setEmailSign(
                getProviderNameDisplay(getUser('data') as any) +
                (getUser('data')?.credential ? `, ${getUser('data')?.credential}` : ''),
            );

            setScheduleReminderTime(
                appointmentEventDetail?.startAt
                    ? dayjs(appointmentEventDetail.startAt).add(14, 'days').valueOf()
                    : undefined,
            );
            setScheduleSmartFollowUpTime(
                appointmentEventDetail?.startAt
                    ? dayjs(appointmentEventDetail.startAt).add(14, 'days').valueOf()
                    : undefined,
            );
            const startAt = dayjs().add(-1, 'days').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
            const endAt = dayjs().add(60, 'days').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
            const fetchData = async () => {
                setLoadingTimeSlot(true);
                const res = await getProviderTimeSlotByLength({ startAt, endAt, length: followUpDuration || 15 });
                setLoadingTimeSlot(false);
                if (!res.error) {
                    const serverSlog: { date: string; length: string }[] = res.data?.data?.timeSlots;
                    const addTime: number[] = [];
                    serverSlog?.forEach((item) => {
                        const date = item.date?.replace(' +0000', 'Z');
                        if (new Date(date).getTime() > new Date().getTime()) {
                            addTime.push(dayjs(date).valueOf());
                        }
                    });
                    setTimeSlotList(addTime);
                    //find default time
                    const expectTime = dayjs(appointmentEventDetail.startAt).add(14, 'days').valueOf();
                    const expectRescheduleTime = dayjs(appointmentEventDetail.startAt).add(1, 'days').valueOf();
                    let defaultTimeForFollowUp = 0;
                    let defaultTimeForRechedule = 0;
                    addTime.forEach((item) => {
                        if (Math.abs(item - expectTime) < Math.abs(defaultTimeForFollowUp - expectTime)) {
                            defaultTimeForFollowUp = item;
                        }
                        if (
                            Math.abs(item - expectRescheduleTime) <
                            Math.abs(defaultTimeForRechedule - expectRescheduleTime)
                        ) {
                            defaultTimeForRechedule = item;
                        }
                    });

                    setDefaultNextFollowUpTime(defaultTimeForFollowUp);
                    setDefaultRescheduleTime(defaultTimeForRechedule);
                }
            };
            fetchData();
        }
    }, [appointmentEventDetail?.id, timezone]);

    let scheduleReminderDateLabel = '';
    let scheduleReminderTimeLabel = '';
    if (scheduleReminderTime) {
        try {
            // let reminderDayjs = dayjs(appointmentEventDetail?.startAt).add(scheduleReminderValue, scheduleReminderUnit?.toLowerCase() as any).hour(scheduleReminderTime?.hour()!).minute(scheduleReminderTime?.minute()!)
            //     .second(0);
            const reminderDayjs = dayjs(scheduleReminderTime);
            // if (timezone) {
            //     reminderDayjs = reminderDayjs.tz(timezone);
            // }
            //Sunday, March 2, 2025
            //0:00 - 10:15 am
            scheduleReminderDateLabel = reminderDayjs.format('dddd, MMMM D, YYYY');
            scheduleReminderTimeLabel = `${reminderDayjs.format(
                reminderDayjs.hour() === 0 ? 'HH:mm' : 'hh:mm',
            )} - ${reminderDayjs
                .add(followUpDuration || 15, 'minute')
                .format(reminderDayjs.hour() === 0 ? 'HH:mm' : 'hh:mm A')}`;
            scheduleReminderTimeLabel = `${scheduleReminderTimeLabel} | ${followUpDuration} mins`;
        } catch (e) {
            console.error('Error calculating schedule reminder date:', e);
            scheduleReminderDateLabel = '';
        }
    }

    let scheduleSmartFollowUpDateLabel = '';
    let scheduleSmartFollowUpTimeLabel = '';
    if (scheduleSmartFollowUpTime) {
        try {
            // let smartFollowDayjs = dayjs(appointmentEventDetail?.startAt).add(scheduleSmartFollowUpValue, scheduleSmartFollowUpUnit?.toLowerCase() as any).hour(scheduleSmartFollowUpTime?.hour()!).minute(scheduleSmartFollowUpTime?.minute()!)
            //     .second(0);
            let smartFollowDayjs = dayjs(scheduleSmartFollowUpTime);
            if (timezone) {
                smartFollowDayjs = smartFollowDayjs.tz(timezone);
            }
            //Sunday, March 2, 2025
            //0:00 - 10:15 am
            scheduleSmartFollowUpDateLabel = smartFollowDayjs.format('dddd, MMMM D, YYYY');
            scheduleSmartFollowUpTimeLabel = `${smartFollowDayjs.format(
                smartFollowDayjs.hour() === 0 ? 'HH:mm' : 'hh:mm',
            )} - ${smartFollowDayjs
                .add(followUpDuration || 0, 'minute')
                .format(smartFollowDayjs.hour() === 0 ? 'HH:mm' : 'hh:mm A')}`;
            scheduleSmartFollowUpTimeLabel = `${scheduleSmartFollowUpTimeLabel} | ${followUpDuration} mins`;
        } catch (e) {
            console.error('Error calculating schedule reminder date:', e);
            scheduleReminderDateLabel = '';
        }
    }

    let followUpServiceLabel = '';
    if (appointmentEventDetail) {
        if (appointmentEventDetail.serviceState) {
            followUpServiceLabel = `${followUpServiceLabel}[${appointmentEventDetail.serviceState}]`;
        }
        if (appointmentEventDetail.serviceSpecialty) {
            followUpServiceLabel = `${followUpServiceLabel} ${appointmentEventDetail.serviceSpecialty}:`;
        }
        followUpServiceLabel = `${followUpServiceLabel} Follow-up`;
        followUpServiceLabel = `${followUpServiceLabel} | ${followUpDuration} mins`;
        followUpServiceLabel = followUpServiceLabel.trim();
    }

    const scheduleReminderValueError = scheduleReminderValue !== undefined && scheduleReminderValue <= 0;
    const scheduleSmartFollowUpValueError = scheduleSmartFollowUpValue !== undefined && scheduleSmartFollowUpValue <= 0;

    const handleConfirm = async () => {
        let startAt = '';
        let endAt = '';
        let messageToPatient = '';
        const reqData: TScheduleAFollowUpVisitByProvider = {
            appointmentId: appointmentEventDetail?.id!,
            rescheduledAction: ERescheduledAction.NEW_APPOINTMENT,
            emailSignature:
                emailSign ||
                getProviderNameDisplay(getUser('data') as any) +
                (getUser('data')?.credential ? `, ${getUser('data')?.credential}` : ''),
            messageToPatient,
            serviceType: EServiceType.ESTABLISHED_PATIENT_VISIT
        };
        if (selectScheduleItemIndex === 1) {
            //smart follow up
            reqData.rescheduledAction = ERescheduledAction.NEW_APPOINTMENT;
            if (scheduleSmartFollowUpTime) {
                //let startAtDayjs = dayjs(appointmentEventDetail?.startAt).add(scheduleSmartFollowUpValue, scheduleSmartFollowUpUnit as dayjs.ManipulateType).hour(scheduleSmartFollowUpTime.hour()).minute(scheduleSmartFollowUpTime.minute());
                let startAtDayjs = dayjs(scheduleSmartFollowUpTime);
                if (timezone) {
                    startAtDayjs = startAtDayjs.tz(timezone);
                }
                startAt = startAtDayjs.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
                endAt = startAtDayjs.add(followUpDuration, 'minute').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
                messageToPatient = scheduleSmartFollowUpText || '';
            }
            let appName: string = `${EServiceType.ESTABLISHED_PATIENT_VISIT}`;
            if (appointmentEventDetail?.serviceSpecialty) {
                appName = `${appointmentEventDetail?.serviceSpecialty}: ${appName}`;
            }
            if (targetClient) {
                appName = `${targetClient.firstName} ${targetClient.lastName} - ${appName}`;
            }
            reqData.externalVideoLink = meetingLink;
        }
        if (selectScheduleItemIndex === 2) {
            //send reminder
            reqData.rescheduledAction = ERescheduledAction.ONLY_REMIND_PATIENT;
            if (scheduleReminderTime) {
                //// let startAtDayjs = dayjs(appointmentEventDetail?.startAt).add(scheduleReminderValue, scheduleReminderUnit as dayjs.ManipulateType).hour(scheduleReminderTime.hour()).minute(scheduleReminderTime.minute());
                let startAtDayjs = dayjs(scheduleReminderTime);
                if (timezone) {
                    startAtDayjs = startAtDayjs.tz(timezone);
                }
                startAt = startAtDayjs.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
                messageToPatient = scheduleReminderText || '';
            }
        }
        //reqData.emailSignature = sign;
        reqData.startAt = startAt;
        reqData.messageToPatient = messageToPatient;

        setSubmiting(true);
        const res = await calendarScheduleAFollowUpVisitByProvider(reqData);
        setSubmiting(false);
        if (!res.error) {
            if (selectScheduleItemIndex === 1) {
                message.success('Follow-up visit scheduled successfully');
                onCancel();
            } else {
                message.success('Reminder sent successfully');
                onCancel();
            }
            onCancel();
        } else {
            message.error(res.error.message);
        }
    };

    const handleSetService = () => {
        window.open(`/${PATH.PPA_SERVICE_TEMPLATE}?from=menu`, '_blank');
        onCancel();
    };

    return (
        <div>
            <Modal
                open={open}
                title="Schedule a follow-up visit"
                wrapClassName={`${s.modalFixHeightWrap} ${showTimeSelectModal ? s.modalFixHeightWrapShowTimeSelectModal : ''
                    }`}
                centered
                onCancel={onCancel}
                footer={
                    <div className={s.cancelBtn}>
                        <Button onClick={onCancel}>Cancal</Button>
                        <Button onClick={handleConfirm} disabled={disabled} type="primary">
                            Confirm
                        </Button>
                    </div>
                }
            >
                <div className={s.showWrapOut}>
                    <div className={s.tContent}>
                        Proactive follow-up helps patients stay engaged with your practice. Choose how you'd like to
                        continue care.
                    </div>
                    <div className={s.tNote}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6ZM9 9C8.44772 9 8 9.44772 8 10C8 10.5523 8.44772 11 9 11V14C9 14.5523 9.44772 15 10 15H11C11.5523 15 12 14.5523 12 14C12 13.4477 11.5523 13 11 13V10C11 9.44772 10.5523 9 10 9H9Z"
                                fill="#60A5FA"
                            />
                        </svg>
                        Our data shows that providers who schedule follow-ups have 35% higher patient retention rates.
                    </div>
                    <div className={`${s.itemWrap} ${selectScheduleItemIndex === 2 ? s.itemWrapSelect2 : ''}`}>
                        <div
                            onClick={() => {
                                setSelectScheduleItemIndex(1);
                            }}
                            className={`${s.mScheduleItem} ${selectScheduleItemIndex === 1 ? s.mScheduleItemSelect : ''
                                }`}
                        >
                            <div className={s.mScheduleItemTitle}>
                                <div className={s.mScheduleItemTitleLeft}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d="M8 10H8.01M12 10H12.01M16 10H16.01M9 16H5C3.89543 16 3 15.1046 3 14V6C3 4.89543 3.89543 4 5 4H19C20.1046 4 21 4.89543 21 6V14C21 15.1046 20.1046 16 19 16H14L9 21V16Z"
                                            stroke="#111827"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    Schedule a smart follow-up
                                </div>
                                <div className={s.mScheduleItemTitleRight}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M11.9984 21.5999C17.3004 21.5999 21.5984 17.3018 21.5984 11.9999C21.5984 6.69797 17.3004 2.3999 11.9984 2.3999C6.6965 2.3999 2.39844 6.69797 2.39844 11.9999C2.39844 17.3018 6.6965 21.5999 11.9984 21.5999ZM16.447 10.4484C16.9156 9.9798 16.9156 9.22 16.447 8.75137C15.9783 8.28275 15.2185 8.28275 14.7499 8.75137L10.7984 12.7028L9.24696 11.1514C8.77833 10.6827 8.01854 10.6827 7.54991 11.1514C7.08128 11.62 7.08128 12.3798 7.54991 12.8484L9.94991 15.2484C10.4185 15.7171 11.1783 15.7171 11.647 15.2484L16.447 10.4484Z"
                                            fill="#00816B"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <div className={s.mScheduleItemDes}>
                                Set a time now — the system will help you schedule a follow-up appointment with the
                                patient.
                            </div>
                            {hasEstablishedService === false && (
                                <div className={s.setUpService}>
                                    <div className={s.setLeft}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8.25706 3.09858C9.02167 1.73928 10.9788 1.73928 11.7434 3.09858L17.3237 13.0191C18.0736 14.3523 17.1102 15.9996 15.5805 15.9996H4.4199C2.89025 15.9996 1.92682 14.3523 2.67675 13.0191L8.25706 3.09858ZM11.0001 12.9998C11.0001 13.552 10.5524 13.9998 10.0001 13.9998C9.44784 13.9998 9.00012 13.552 9.00012 12.9998C9.00012 12.4475 9.44784 11.9998 10.0001 11.9998C10.5524 11.9998 11.0001 12.4475 11.0001 12.9998ZM10.0001 4.99976C9.44784 4.99976 9.00012 5.44747 9.00012 5.99976V8.99976C9.00012 9.55204 9.44784 9.99976 10.0001 9.99976C10.5524 9.99976 11.0001 9.55204 11.0001 8.99976V5.99976C11.0001 5.44747 10.5524 4.99976 10.0001 4.99976Z"
                                                fill="#FBBF24"
                                            />
                                        </svg>
                                    </div>
                                    <div className={s.setRight}>
                                        <div className={s.sTitle}>Add a follow-up service to continue</div>
                                        <div className={s.sDes}>
                                            You haven’t added a follow-up visit type for this specialty yet. To
                                            continue, please{' '}
                                            <span onClick={handleSetService} className={s.link}>
                                                go to your service settings
                                            </span>{' '}
                                            and set it up first.
                                        </div>
                                    </div>
                                </div>
                            )}
                            {hasEstablishedService === undefined && <Spin />}
                            {hasEstablishedService === true && (
                                <>
                                    <div className={s.mScheduleItemSumTitle}>Appointment</div>
                                    <div className={s.mScheduleItemSum}>
                                        <div className={s.sumDay}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="25"
                                                viewBox="0 0 24 25"
                                                fill="none"
                                            >
                                                <path
                                                    d="M8 7.1748V3.1748M16 7.1748V3.1748M7 11.1748H17M5 21.1748H19C20.1046 21.1748 21 20.2794 21 19.1748V7.1748C21 6.07024 20.1046 5.1748 19 5.1748H5C3.89543 5.1748 3 6.07024 3 7.1748V19.1748C3 20.2794 3.89543 21.1748 5 21.1748Z"
                                                    stroke="#6B7280"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            {scheduleSmartFollowUpDateLabel}
                                            <span
                                                onClick={() => {
                                                    setShowTimeSelectModal(true);
                                                }}
                                                className={s.changeTime}
                                            >
                                                Change time
                                            </span>
                                        </div>
                                        <div className={s.sumTime}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="25"
                                                viewBox="0 0 24 25"
                                                fill="none"
                                            >
                                                <path
                                                    d="M12 8.1748V12.1748L15 15.1748M21 12.1748C21 17.1454 16.9706 21.1748 12 21.1748C7.02944 21.1748 3 17.1454 3 12.1748C3 7.20424 7.02944 3.1748 12 3.1748C16.9706 3.1748 21 7.20424 21 12.1748Z"
                                                    stroke="#6B7280"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            {scheduleSmartFollowUpTimeLabel}
                                        </div>
                                        <div className={s.sumTime}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                            >
                                                <path
                                                    d="M17.6569 16.6569C16.7202 17.5935 14.7616 19.5521 13.4138 20.8999C12.6327 21.681 11.3677 21.6814 10.5866 20.9003C9.26234 19.576 7.34159 17.6553 6.34315 16.6569C3.21895 13.5327 3.21895 8.46734 6.34315 5.34315C9.46734 2.21895 14.5327 2.21895 17.6569 5.34315C20.781 8.46734 20.781 13.5327 17.6569 16.6569Z"
                                                    stroke="#6B7280"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                                <path
                                                    d="M15 11C15 12.6569 13.6569 14 12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C13.6569 8 15 9.34315 15 11Z"
                                                    stroke="#6B7280"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            Video Call
                                        </div>
                                        <a href={appointmentEventDetail?.externalVideoLink} className={s.sumVideoLink}>
                                            {appointmentEventDetail?.externalVideoLink}
                                        </a>
                                        {appointmentEventDetail?.contactType === EContactTypeV2.IN_PERSON &&
                                            appointmentEventDetail?.location && (
                                                <div>
                                                    <div className={s.sumTime}>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="24"
                                                            height="24"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                        >
                                                            <path
                                                                d="M17.6569 16.6569C16.7202 17.5935 14.7616 19.5521 13.4138 20.8999C12.6327 21.681 11.3677 21.6814 10.5866 20.9003C9.26234 19.576 7.34159 17.6553 6.34315 16.6569C3.21895 13.5327 3.21895 8.46734 6.34315 5.34315C9.46734 2.21895 14.5327 2.21895 17.6569 5.34315C20.781 8.46734 20.781 13.5327 17.6569 16.6569Z"
                                                                stroke="#6B7280"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                            <path
                                                                d="M15 11C15 12.6569 13.6569 14 12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C13.6569 8 15 9.34315 15 11Z"
                                                                stroke="#6B7280"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                        In-person visit
                                                    </div>
                                                    <div className={s.sumValue}>{appointmentEventDetail?.location}</div>
                                                </div>
                                            )}
                                        {appointmentEventDetail?.contactType === EContactTypeV2.TELEPHONE &&
                                            appointmentEventDetail?.telephone && (
                                                <>
                                                    <div className={s.sumTime}>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="24"
                                                            height="24"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                        >
                                                            <path
                                                                d="M17.6569 16.6569C16.7202 17.5935 14.7616 19.5521 13.4138 20.8999C12.6327 21.681 11.3677 21.6814 10.5866 20.9003C9.26234 19.576 7.34159 17.6553 6.34315 16.6569C3.21895 13.5327 3.21895 8.46734 6.34315 5.34315C9.46734 2.21895 14.5327 2.21895 17.6569 5.34315C20.781 8.46734 20.781 13.5327 17.6569 16.6569Z"
                                                                stroke="#6B7280"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                            <path
                                                                d="M15 11C15 12.6569 13.6569 14 12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C13.6569 8 15 9.34315 15 11Z"
                                                                stroke="#6B7280"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                        Telephone visit
                                                    </div>
                                                    <div className={s.sumValue}>{meetingLink}</div>
                                                </>
                                            )}
                                        <div className={s.sumTime}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                            >
                                                <path
                                                    d="M21 13.2554C18.2207 14.3805 15.1827 15 12 15C8.8173 15 5.7793 14.3805 3 13.2554M16 6V4C16 2.89543 15.1046 2 14 2H10C8.89543 2 8 2.89543 8 4V6M12 12H12.01M5 20H19C20.1046 20 21 19.1046 21 18V8C21 6.89543 20.1046 6 19 6H5C3.89543 6 3 6.89543 3 8V18C3 19.1046 3.89543 20 5 20Z"
                                                    stroke="#6B7280"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            🔄 Established patient visit
                                        </div>
                                        {appointmentEventDetail?.serviceSpecialty && (
                                            <div className={s.sumTime}>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M19 21V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V21M19 21L21 21M19 21H14M5 21L3 21M5 21H10M9 6.99998H10M9 11H10M14 6.99998H15M14 11H15M10 21V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V21M10 21H14"
                                                        stroke="#6B7280"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                                {appointmentEventDetail?.serviceSpecialty}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            <div className={s.msgToWrap}>
                                <div className={s.mtTitle}>Message to patient</div>
                                <div className={s.mtContent}>
                                    <div className={s.mtHeader}>Dear {targetClient?.firstName},</div>
                                    <div className={s.mtDes}>
                                        Following our recent{' '}
                                        <span style={{ color: '#000' }}>{appointmentEventDetail?.serviceType}</span> on{' '}
                                        <span style={{ color: '#000' }}>
                                            {appointmentDate?.format('MMM D, YYYY')} at{' '}
                                            {dayjs(appointmentEventDetail?.startAt!)?.format('hh:mm A')}
                                        </span>
                                        , your next follow-up appointment has been scheduled.
                                        <br />
                                        <br />
                                        If the new time doesn’t work for you, you can reschedule or message me directly
                                        in your patient portal.
                                    </div>
                                    <div className={s.mtCus}>
                                        <div className={s.mtCusTitle}>
                                            Customized message to patient{' '}
                                            <span style={{ color: '#8E8E8E' }}>(optional)</span>
                                        </div>
                                        <div className={s.mtCusValue}>
                                            <Input.TextArea
                                                onChange={(e) => {
                                                    setScheduleSmartFollowUpText(e.target.value);
                                                }}
                                                placeholder="Add customized message..."
                                                value={scheduleSmartFollowUpText}
                                                rows={4}
                                            />
                                        </div>
                                    </div>
                                    <div className={s.mtFooter}>
                                        <p>Looking forward to our visit,</p>
                                        <p className={s.mSign}>Email signature</p>
                                        <p>
                                            <Input
                                                value={emailSign}
                                                onChange={(e) => {
                                                    setEmailSign(e.target.value);
                                                }}
                                                placeholder={`${getProviderNameDisplay(getUser('data') as any)}${getUser('data')?.credential
                                                    ? `, ${getUser('data')?.credential}`
                                                    : ''
                                                    }`}
                                            />
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            onClick={() => {
                                setSelectScheduleItemIndex(2);
                            }}
                            className={`${s.mScheduleItem} ${selectScheduleItemIndex === 2 ? s.mScheduleItemSelect : ''
                                }`}
                        >
                            <div className={s.mScheduleItemTitle}>
                                <div className={s.mScheduleItemTitleLeft}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d="M3 8L10.8906 13.2604C11.5624 13.7083 12.4376 13.7083 13.1094 13.2604L21 8M5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19Z"
                                            stroke="#111827"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    Send patient a reminder to schedule a follow-up
                                </div>
                                <div className={s.mScheduleItemTitleRight}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M11.9984 21.5999C17.3004 21.5999 21.5984 17.3018 21.5984 11.9999C21.5984 6.69797 17.3004 2.3999 11.9984 2.3999C6.6965 2.3999 2.39844 6.69797 2.39844 11.9999C2.39844 17.3018 6.6965 21.5999 11.9984 21.5999ZM16.447 10.4484C16.9156 9.9798 16.9156 9.22 16.447 8.75137C15.9783 8.28275 15.2185 8.28275 14.7499 8.75137L10.7984 12.7028L9.24696 11.1514C8.77833 10.6827 8.01854 10.6827 7.54991 11.1514C7.08128 11.62 7.08128 12.3798 7.54991 12.8484L9.94991 15.2484C10.4185 15.7171 11.1783 15.7171 11.647 15.2484L16.447 10.4484Z"
                                            fill="#00816B"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <div className={s.mScheduleItemDes}>
                                A reminder will be sent to the patient in{' '}
                                {dayjs(scheduleReminderTime).diff(dayjs(), 'days')} {scheduleReminderUnit}. You can
                                update the time if needed.
                            </div>
                            <div className={s.mScheduleItemSumTitle}>Recommend time</div>
                            <div className={s.mScheduleItemSum}>
                                <div className={s.sumDay}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="25"
                                        viewBox="0 0 24 25"
                                        fill="none"
                                    >
                                        <path
                                            d="M8 7.1748V3.1748M16 7.1748V3.1748M7 11.1748H17M5 21.1748H19C20.1046 21.1748 21 20.2794 21 19.1748V7.1748C21 6.07024 20.1046 5.1748 19 5.1748H5C3.89543 5.1748 3 6.07024 3 7.1748V19.1748C3 20.2794 3.89543 21.1748 5 21.1748Z"
                                            stroke="#6B7280"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    {scheduleReminderDateLabel}
                                    <span
                                        onClick={() => {
                                            setShowTimeSelectModal(true);
                                        }}
                                        className={s.changeTime}
                                    >
                                        Change time
                                    </span>
                                </div>
                                <div className={s.sumTime}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="25"
                                        viewBox="0 0 24 25"
                                        fill="none"
                                    >
                                        <path
                                            d="M12 8.1748V12.1748L15 15.1748M21 12.1748C21 17.1454 16.9706 21.1748 12 21.1748C7.02944 21.1748 3 17.1454 3 12.1748C3 7.20424 7.02944 3.1748 12 3.1748C16.9706 3.1748 21 7.20424 21 12.1748Z"
                                            stroke="#6B7280"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    {scheduleReminderTimeLabel}
                                    {timeZoneLabel ? ` (${timeZoneLabel})` : ''}
                                </div>
                            </div>
                            <div className={s.msgToWrap}>
                                <div className={s.mtTitle}>Message to patient</div>
                                <div className={s.mtContent}>
                                    <div className={s.mtHeader}>Dear {targetClient?.firstName},</div>
                                    <div className={s.mtDes}>
                                        Just a quick note following our recent visit{' '}
                                        <span style={{ color: '#000' }}>
                                            {appointmentDate?.format('MMM D, YYYY')} at{' '}
                                            {dayjs(appointmentEventDetail?.startAt!)?.format('hh:mm A')}
                                        </span>
                                        .
                                    </div>
                                    <div className={s.mtCus}>
                                        <div className={s.mtCusTitle}>
                                            Customized message to patient{' '}
                                            <span style={{ color: '#8E8E8E' }}>(optional)</span>
                                        </div>
                                        <div className={s.mtCusValue}>
                                            <Input.TextArea
                                                onChange={(e) => {
                                                    setScheduleReminderText(e.target.value);
                                                }}
                                                ref={scheduleReminderAreaRef}
                                                value={scheduleReminderText}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Tab' && !scheduleReminderText) {
                                                        e.preventDefault(); // 防止默认的Tab行为（切换焦点）
                                                        setScheduleReminderText(
                                                            'To support your ongoing care, I recommend scheduling your next visit. You can choose a time that works best for you.',
                                                        );
                                                    }
                                                }}
                                                rows={4}
                                            />

                                            {!scheduleReminderText && (
                                                <div
                                                    onClick={() => {
                                                        scheduleReminderAreaRef?.current?.focus();
                                                    }}
                                                    className={s.placeHolder}
                                                >
                                                    To support your ongoing care, I recommend scheduling your next
                                                    visit. You can choose a time that works best for you.
                                                    <span className={s.placeHolderText}>
                                                        Press “Tab” to use the text
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className={s.mtFooter}>
                                        <p>Looking forward to our visit,</p>
                                        <p className={s.mSign}>Email signature</p>
                                        <p>
                                            <Input
                                                value={emailSign}
                                                onChange={(e) => {
                                                    setEmailSign(e.target.value);
                                                }}
                                                placeholder={`${getProviderNameDisplay(getUser('data') as any)}${getUser('data')?.credential
                                                    ? `, ${getUser('data')?.credential}`
                                                    : ''
                                                    }`}
                                            />
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
            <Modal
                open={showTimeSelectModal}
                onCancel={() => {
                    setScheduleReminderTime(defaultNextFollowUpTime);
                    setScheduleSmartFollowUpTime(defaultNextFollowUpTime);
                    setShowTimeSelectModal(false);
                }}
                className={s.modalWrap}
                wrapClassName="practice"
                width={660}
                onOk={() => {
                    setShowTimeSelectModal(false);
                }}
            >
                <div className={s.modalWrap}>
                    <div className={`${s.timePicker} ${s.timePickerShow}`}>
                        <div className={s.modalTitle}>
                            <h3>Select date and time</h3>
                            <div className={s.subTitle}>{timeZoneLabel ? `Timezone: ${timeZoneLabel}` : ''}</div>
                        </div>
                        {selectScheduleItemIndex === 1 && (
                            <div className={s.modalService}>
                                <span style={{ color: '#0E62F4' }}>Service:</span> {followUpServiceLabel}
                            </div>
                        )}
                        <div className={s.timePickerIn}>
                            <TimePicker
                                idSub="modal"
                                loading={loadingTimeSlot}
                                onChange={(time: number) => {
                                    if (selectScheduleItemIndex === 1) {
                                        setScheduleSmartFollowUpTime(time);
                                    } else {
                                        setScheduleReminderTime(time);
                                    }
                                }}
                                timeSlotList={timeSlotList}
                                timezone={timezone}
                                defaultValue={defaultNextFollowUpTime}
                                extraTop={selectScheduleItemIndex === 1 ? 50 : 0}
                            />
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default FollowUpModal;
